import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { clearAuthStorage, getAccessToken } from '@/lib/authStorage';

// 错误处理方案：
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  data: any;
  msg?: string;
  errorCode?: number;
  errorMessage?: string;
  showType?: number;
}

/**
 * 业务错误
 */
class BizError extends Error {
  info: ResponseStructure;
  constructor(info: ResponseStructure) {
    super(info.msg || info.errorMessage);
    this.name = 'BizError';
    this.info = info;
  }
}

/**
 * @name 错误处理
 * pro 自带的错误处理
 * @doc https://umijs.org/docs/max/request
 */
export const errorConfig: RequestConfig = {
  // 错误处理
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, data, msg, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (code !== 0) {
        const error = new BizError({
          code,
          data,
          msg,
          errorCode,
          errorMessage,
          showType,
        });
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // BizError 业务错误
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { msg, errorMessage } = errorInfo;
          const errorText = msg || errorMessage || '请求错误';

          switch (errorInfo.showType) {
            case 0:
              // silent
              break;
            case 1:
              message.warning(errorText);
              break;
            case 2:
              message.error(errorText);
              break;
            case 4:
              notification.open({
                description: errorText,
                message: '请求错误',
              });
              break;
            case 9:
              // redirect
              break;
            default:
              message.error(errorText);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const status = error.response.status;
        if (status === 401) {
          // 清除认证信息
          clearAuthStorage();
          // 重定向到登录页
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
        message.error(`请求错误 ${status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        message.error('网络异常，请检查网络连接');
      } else {
        // 发送请求时出了点问题
        message.error('请求异常');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理
      const token = getAccessToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      // 拦截响应数据
      const { data } = response as unknown as { data: ResponseStructure };

      if (data && data.code !== 0) {
        message.error(data.msg || '请求失败');
      }

      return response;
    },
  ],
};
